import { Directive, ElementRef, HostListener, Renderer2, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DecimalPipe } from '@angular/common';

@Directive({
  standalone: false,
  selector: '[appCurrencyInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputDirective),
      multi: true,
    },
    DecimalPipe
  ],
})
export class CurrencyInputDirective implements ControlValueAccessor {
  @Input() decimalPlaces: number = 2; // Default is 2 decimal places

  private onChange: any;
  private onTouched: any;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private decimalPipe: DecimalPipe
  ) {}

  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    let numericValue = this.normalizeNumericValue(value);
    const decimalPattern = `^(\\d{0,10})(\\.\\d{0,${this.decimalPlaces}})?$`;
    const regex = new RegExp(decimalPattern);
    const match = numericValue.match(regex);

    if (match) {
      numericValue = match[0];
    } else {
      numericValue = numericValue.substring(0, 13); // Enforce max limit
    }

    this.renderer.setProperty(this.el.nativeElement, 'value', numericValue);

    if (this.onChange) {
      this.onChange(parseFloat(numericValue));
    }
  }

  @HostListener('blur')
  onBlur(): void {
    let numericValue = this.normalizeNumericValue(this.el.nativeElement.value);
    if (!numericValue) {
      numericValue = '0.000';
    } else {
      const decimalPattern = `^(\\d{0,10})(\\.\\d{0,${this.decimalPlaces}})?$`;
      const regex = new RegExp(decimalPattern);
      const match = numericValue.match(regex);

      if (match) {
        numericValue = match[0];
      } else {
        numericValue = numericValue.substring(0, 13);
      }
    }

    const formattedValue = this.formatNumber(numericValue);
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);

    if (this.onChange) {
      this.onChange(parseFloat(numericValue));
    }

    if (this.onTouched) {
      this.onTouched();
    }
  }

  private formatNumber(value: string): string {
      const parsedValue = parseFloat(this.normalizeNumericValue(value));
      if (isNaN(parsedValue)) {
        return '';
      }
      return this.decimalPipe.transform(parsedValue, `1.${this.decimalPlaces}-${this.decimalPlaces}`) || '';
  }

  private normalizeNumericValue(value: string): string {
    return (value || '')
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/[^0-9.]/g, '');
  }

  writeValue(value: any): void {
    const formattedValue = this.formatNumber(value !== null && value !== undefined ? value.toString() : '0');
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue || '');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }
}
